// Public-site content loaders.
//
// Every hook reads from the single /cache/snapshot endpoint (one shared fetch —
// see getSnapshot below) rather than hitting /blogs, /works, /experiences and
// /settings separately.
//
// The bundled static data is a fallback for an UNREACHABLE API only. It is
// deliberately not used when the API responds with an empty list: guarding on
// `.length` would make deleting the last item impossible to reflect, silently
// resurrecting placeholder content on the live site.
import { useEffect, useState } from 'react';
import { api, type Blog as ApiBlog, type CacheSnapshot, type Experience as ApiExperience, type SectionTaglines, type SiteSettings, type Work as ApiWork } from './api';
import { blogs as staticBlogs, type Blog as PublicBlog } from '../data/blogs';
import { experiences as staticExperiences } from '../data/experiences';
import type { ExperienceItem } from '../components/ui/horizontal-experience';
import { setVisitorInfo } from './analytics';

// Public reads go through the Cloudflare Worker (Upstash-backed) so visitors
// never wait on a cold-started backend. It also returns edge-resolved visitor
// geo, which the browser can't determine on its own. Falls back to the origin
// API if the Worker is unset or unreachable.
const SNAPSHOT_URL = (import.meta.env.VITE_SNAPSHOT_URL ?? '').trim();

// One in-flight/resolved fetch shared by every hook on the page — mounting
// BlogsPage, WorksPage, and SiteLayout together still only hits the API once.
let snapshotPromise: Promise<CacheSnapshot> | null = null;

async function loadSnapshot(): Promise<CacheSnapshot> {
  if (SNAPSHOT_URL) {
    try {
      const res = await fetch(SNAPSHOT_URL);
      if (!res.ok) throw new Error(`worker ${res.status}`);
      const snap = (await res.json()) as CacheSnapshot;
      setVisitorInfo(snap.visitor);
      return snap;
    } catch {
      // Worker down / misconfigured — fall through to the origin.
    }
  }
  return api.cache.snapshot();
}

function getSnapshot(): Promise<CacheSnapshot> {
  if (!snapshotPromise) snapshotPromise = loadSnapshot().catch(err => { snapshotPromise = null; throw err; });
  return snapshotPromise;
}

// ── Mappers: API shape → the shape the public components already expect ──

function mapBlog(b: ApiBlog): PublicBlog {
  const iso = b.date_published ?? b.created_at;
  return {
    slug: b.slug,
    title: b.title,
    excerpt: b.excerpt ?? '',
    tags: b.tags,
    reading: b.reading ?? '',
    date: iso ? new Date(iso).toLocaleString('en-US', { month: 'short', year: 'numeric' }) : '',
    datePublished: iso ? iso.slice(0, 10) : '',
    category: b.category ?? 'General',
    image: b.image ?? '',
    content: b.content,
  };
}

function mapExperience(e: ApiExperience): ExperienceItem {
  return {
    slug: e.slug,
    year: e.year,
    title: e.title,
    role: e.role,
    logo: e.logo ?? '',
    image: e.image ?? undefined,
    location: e.location ?? undefined,
    industry: e.industry ?? undefined,
    website: e.website ?? undefined,
    description: e.description ?? undefined,
    projects: e.projects,
  };
}

export interface WorkItem {
  slug: string;
  title: string;
  role: string;
  desc: string;
  bullets: string[];
  tags: string[];
  link?: string;
  image?: string;
}
function mapWork(w: ApiWork): WorkItem {
  return {
    slug: w.slug,
    title: w.title,
    role: w.role,
    desc: w.description ?? '',
    image: w.image ?? undefined,
    bullets: w.bullets,
    tags: w.tags,
    link: w.link ?? undefined,
  };
}

// ── Hooks ──

export function useBlogs() {
  const [blogs, setBlogs] = useState<PublicBlog[]>(staticBlogs);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getSnapshot()
      .then(snap => { if (alive) setBlogs(snap.blogs.filter(r => r.published).map(mapBlog)); })
      .catch(() => { /* keep static fallback */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { blogs, loading };
}

export function useBlog(slug: string | undefined) {
  const fallback = staticBlogs.find(blog => blog.slug === slug);
  const [blog, setBlog] = useState<PublicBlog | undefined>(fallback);
  const [loading, setLoading] = useState(Boolean(slug));

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    getSnapshot()
      .then(snap => {
        const row = snap.blogs.find(b => b.slug === slug);
        if (alive && row && row.published) setBlog(mapBlog(row));
      })
      .catch(() => { /* keep bundled fallback */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  return { blog, loading };
}

export function useExperiences() {
  const [experiences, setExperiences] = useState<ExperienceItem[]>(staticExperiences);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getSnapshot()
      .then(snap => { if (alive) setExperiences(snap.experiences.map(mapExperience)); })
      .catch(() => { /* keep static fallback */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { experiences, loading };
}

export function useExperience(slug: string | undefined) {
  const fallback = staticExperiences.find(experience => experience.slug === slug);
  const [experience, setExperience] = useState<ExperienceItem | undefined>(fallback);
  const [loading, setLoading] = useState(Boolean(slug));

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    getSnapshot()
      .then(snap => {
        const row = snap.experiences.find(e => e.slug === slug);
        if (alive && row) setExperience(mapExperience(row));
      })
      .catch(() => { /* keep bundled fallback */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  return { experience, loading };
}

const STATIC_WORKS: WorkItem[] = [
  {
    slug: 'agent-assist-copilot',
    title: 'Agent Assist — Enterprise AI Copilot',
    role: 'HashedIn by Deloitte',
    desc: 'Engineered enterprise OAuth2 + LLM gateway infrastructure and an observability pipeline serving thousands of developers on a Fortune 500 client platform.',
    bullets: [
      'Built org-wide OAuth2 auth and a secure LLM gateway routing layer under strict enterprise/financial compliance',
      'Architected an observability pipeline processing 1M+ weekly logs at <50ms overhead',
      'Deployed MCP servers to auto-generate architecture diagrams, docs, and test plans',
    ],
    tags: ['Python', 'TypeScript', 'LangGraph', 'MCP', 'OAuth2'],
  },
  {
    slug: 'enterprise-marketing-agent',
    title: 'Enterprise Marketing Agent',
    role: 'HashedIn by Deloitte',
    desc: 'Built a distributed multi-agent content generation system for enterprise marketing teams, cutting content creation time dramatically.',
    bullets: [
      'Designed a 3-agent distributed system reducing content creation time by 80%',
      'Powers 500+ multi-channel campaign assets generated weekly at scale',
    ],
    tags: ['GCP', 'LangChain', 'FastAPI', 'Distributed Systems'],
  },
  {
    slug: 'agentv2-framework',
    title: 'AgentV2 — Autonomous Agent Framework',
    role: 'Open Source Project',
    desc: 'A stateful multi-step reasoning agent framework with a reflection loop, published to PyPI with an active developer community.',
    bullets: [
      'Cut task-completion failures by 45% via reflection-loop reasoning',
      'Reduced end-to-end latency by 35% through parallel sub-agent execution',
    ],
    tags: ['Python', 'LangGraph', 'PyPI', 'Agent Systems'],
  },
  {
    slug: 'oneforall-mcp-server',
    title: 'OneForAll MCP Server',
    role: 'Open Source Project (GPL v3)',
    desc: 'A per-user MCP server architecture with a modular plugin registry, supporting isolated concurrent sessions at scale.',
    bullets: [
      'Architected support for 100+ concurrent isolated user sessions',
      'Built a modular plugin registry for extensible tool integration',
    ],
    tags: ['Python', 'MCP', 'Docker', 'Open Source'],
  },
];

export function useWorks() {
  const [works, setWorks] = useState<WorkItem[]>(STATIC_WORKS);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getSnapshot()
      .then(snap => { if (alive) setWorks(snap.works.map(mapWork)); })
      .catch(() => { /* keep static fallback */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { works, loading };
}

export function useAboutSnapshot() {
  const [about, setAbout] = useState<{ content: string; photo: string | null } | null>(null);
  const [experiences, setExperiences] = useState<ExperienceItem[]>(staticExperiences);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getSnapshot()
      .then(snap => {
        if (!alive) return;
        setAbout(snap.about);
        setSettings(snap.settings);
        setExperiences(snap.experiences.map(mapExperience));
      })
      .catch(() => { /* keep static fallback */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { about, experiences, settings, loading };
}

// Site settings (maintenance mode, resume link, availability, location) —
// used by App.tsx and SiteLayout, also sourced from the shared snapshot.
export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getSnapshot()
      .then(snap => { if (alive) setSettings(snap.settings); })
      .catch(() => { /* no fallback — maintenance mode just stays off */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { settings, loading };
}

/** AI-written section invitations for the chat nudge, regenerated with the
 *  snapshot so they never describe content that no longer exists. */
export function useTaglines() {
  const [taglines, setTaglines] = useState<SectionTaglines | null>(null);
  useEffect(() => {
    let alive = true;
    getSnapshot()
      .then((snap) => { if (alive && snap.taglines) setTaglines(snap.taglines); })
      .catch(() => { /* caller falls back to its static copy */ });
    return () => { alive = false; };
  }, []);
  return taglines;
}
